import React, { useState, useEffect } from 'react';
import './TasteProfile.css';

function TasteProfile({ API_URL, userId, userLikes }) {
  const [taste, setTaste] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountStatus, setAccountStatus] = useState({
    spotify_connected: false,
    youtube_connected: false
  });

  useEffect(() => {
    if (userId) {
      loadTaste();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadAccountStatus = async () => {
    try {
      const apiUrl = API_URL || process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
      const response = await fetch(`${apiUrl}/api/accounts/status?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data);
        return data;
      }
    } catch (err) {
      console.error('Error loading account status:', err);
    }
    return { spotify_connected: false, youtube_connected: false };
  };

  const loadTaste = async () => {
    setLoading(true);
    setError('');
    try {
      // Get API URL - use prop or fallback to environment variable
      const apiUrl = API_URL || process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
      
      // First, check account status
      const status = await loadAccountStatus();
      
      // First, try to get existing taste data from Supabase
      const getResponse = await fetch(`${apiUrl}/api/taste/get?user_id=${userId}`);
      if (getResponse.ok) {
        const existingData = await getResponse.json();
        if (existingData.taste) {
          // If both sources are connected but taste data doesn't have accurate source info,
          // trigger a fresh analysis to get accurate source tracking
          const hasSourceInfo = existingData.taste.sources && existingData.taste.sources.length > 0;
          const bothConnected = status.youtube_connected && status.spotify_connected;
          const sourceDetails = existingData.taste.source_details || {};
          
          // Check if source details look accurate (different counts for each source)
          const hasAccurateSourceInfo = hasSourceInfo && bothConnected && 
            sourceDetails.spotify && sourceDetails.youtube &&
            (sourceDetails.spotify.artist_count !== sourceDetails.youtube.artist_count ||
             sourceDetails.spotify.note); // If there's a note, it's approximate
          
          // Always trigger fresh analysis if both are connected and counts are the same (indicates approximate data)
          const countsMatch = sourceDetails.spotify && sourceDetails.youtube && 
            sourceDetails.spotify.artist_count === sourceDetails.youtube.artist_count;
          
          if (bothConnected && (!hasSourceInfo || !hasAccurateSourceInfo || countsMatch)) {
            console.log('[TASTE] Both sources connected but taste data missing/approximate source info - triggering fresh analysis');
            console.log('[TASTE] Existing source info:', existingData.taste.sources, existingData.taste.source_details);
            console.log('[TASTE] Counts match:', countsMatch, '- this indicates approximate data, forcing fresh analysis');
            // Don't return, continue to analysis below
          } else {
            console.log('[TASTE] Using existing taste data with accurate source info');
            setTaste(existingData.taste);
            setLoading(false);
            return;
          }
        }
      }
      
      // If no existing taste data, check if user has stored YouTube/Spotify tokens
      // and automatically try to analyze taste using stored tokens
      // If both are connected, analyze both and merge results
      if (status.youtube_connected && status.spotify_connected) {
        console.log('[TASTE] Both YouTube and Spotify connected - analyzing both sources together');
        try {
          // Analyze both sources together
          const analyzeResponse = await fetch(`${apiUrl}/api/taste/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              user_id: userId, 
              source: 'both'  // Analyze both Spotify and YouTube
            }),
            credentials: 'include'
          });
          
          console.log('[TASTE] Analysis response status:', analyzeResponse.status);
          
          if (analyzeResponse.ok) {
            const analyzeData = await analyzeResponse.json();
            if (analyzeData.taste) {
              const artistCount = Object.keys(analyzeData.taste.artists || {}).length;
              console.log(`[TASTE] Analysis complete! Found ${artistCount} artists from both sources`);
              console.log('[TASTE] Artist list:', Object.keys(analyzeData.taste.artists || {}).slice(0, 20));
              setTaste(analyzeData.taste);
              setLoading(false);
              return;
            }
          } else {
            const errorText = await analyzeResponse.text();
            console.error('[TASTE] Analysis failed:', errorText);
          }
        } catch (analyzeErr) {
          console.error('[TASTE] Could not auto-analyze with both sources:', analyzeErr);
          // Fall back to individual analysis
        }
      }
      
      // If only one source is connected, analyze that one
      if (status.youtube_connected) {
        console.log('[TASTE] Only YouTube connected - analyzing YouTube only');
        try {
          const analyzeResponse = await fetch(`${apiUrl}/api/taste/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              user_id: userId, 
              source: 'youtube' 
            }),
            credentials: 'include'
          });
          
          if (analyzeResponse.ok) {
            const analyzeData = await analyzeResponse.json();
            if (analyzeData.taste) {
              setTaste(analyzeData.taste);
              setLoading(false);
              return;
            }
          }
        } catch (analyzeErr) {
          console.log('Could not auto-analyze with YouTube:', analyzeErr);
        }
      }
      
      if (status.spotify_connected) {
        console.log('[TASTE] Only Spotify connected - analyzing Spotify only');
        try {
          const analyzeResponse = await fetch(`${apiUrl}/api/taste/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              user_id: userId, 
              source: 'spotify' 
            }),
            credentials: 'include'
          });
          
          if (analyzeResponse.ok) {
            const analyzeData = await analyzeResponse.json();
            if (analyzeData.taste) {
              const artistCount = Object.keys(analyzeData.taste.artists || {}).length;
              console.log(`[TASTE] Analysis complete! Found ${artistCount} artists from Spotify`);
              setTaste(analyzeData.taste);
              setLoading(false);
              return;
            }
          }
        } catch (analyzeErr) {
          console.error('[TASTE] Could not auto-analyze with Spotify:', analyzeErr);
        }
      }
      
      // If no existing data and auto-analysis failed, show message to connect music service
      setError('');
    } catch (err) {
      console.error('Error checking for existing taste:', err);
      if (err.message === 'Failed to fetch') {
        setError(`Cannot connect to backend server. Please check that the API is running.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const connectYouTube = async () => {
    try {
      // Trigger YouTube login flow - pass user_id so token is stored in Supabase
      const response = await fetch(`${API_URL}/youtube-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      if (!response.ok) throw new Error('Failed to initiate YouTube login');
      
      const data = await response.json();
      if (data.auth_url) {
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'YouTube Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        window.addEventListener('message', async (event) => {
          console.log('Received message from YouTube popup:', event.data);
          if (event.data && event.data.type === 'youtube-login-success') {
            if (popup) popup.close();
            // Now analyze taste with YouTube data
            await analyzeTaste('youtube', event.data.token);
          } else if (event.data && event.data.type === 'youtube-login-error') {
            if (popup) popup.close();
            const errorMsg = event.data.error || 'YouTube authentication failed';
            setError(`YouTube authentication failed: ${errorMsg}`);
            console.error('YouTube auth error:', event.data);
          } else if (event.data === 'youtube-login-error') {
            // Fallback for string error message
            if (popup) popup.close();
            setError('YouTube authentication failed');
          }
        }, { once: true });
        
        // Timeout fallback - if popup doesn't send message within 5 minutes, assume failure
        setTimeout(() => {
          if (popup && !popup.closed) {
            popup.close();
            setError('YouTube authentication timed out. Please try again.');
          }
        }, 300000); // 5 minutes
      }
    } catch (err) {
      setError('Failed to connect YouTube Music');
      console.error(err);
    }
  };

  const connectSpotify = async () => {
    try {
      const response = await fetch(`${API_URL}/spotify-login`);
      if (!response.ok) throw new Error('Failed to initiate Spotify login');
      
      const data = await response.json();
      if (data.auth_url) {
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'Spotify Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        window.addEventListener('message', async (event) => {
          if (event.data === 'spotify-login-success') {
            if (popup) popup.close();
            // Now analyze taste with Spotify data
            await analyzeTaste('spotify');
          } else if (event.data === 'spotify-login-error') {
            if (popup) popup.close();
            setError('Spotify authentication failed');
          }
        }, { once: true });
      }
    } catch (err) {
      setError('Failed to connect Spotify');
      console.error(err);
    }
  };

  const forceRefreshAnalysis = async () => {
    console.log('[TASTE] Force refreshing analysis...');
    setLoading(true);
    setError('');
    
    // Get current account status to determine which source(s) to use
    const status = await loadAccountStatus();
    
    if (status.youtube_connected && status.spotify_connected) {
      console.log('[TASTE] Both sources connected - analyzing both');
      await analyzeTaste('both');
    } else if (status.youtube_connected) {
      console.log('[TASTE] Only YouTube connected - analyzing YouTube');
      await analyzeTaste('youtube');
    } else if (status.spotify_connected) {
      console.log('[TASTE] Only Spotify connected - analyzing Spotify');
      await analyzeTaste('spotify');
    } else {
      setError('No connected accounts found. Please connect Spotify or YouTube Music first.');
      setLoading(false);
    }
  };

  const analyzeTaste = async (source, youtubeToken = null) => {
    setLoading(true);
    setError('');
    try {
      // Get API URL - use prop or fallback to environment variable
      const apiUrl = API_URL || process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
      
      // If we have userLikes, we can use stored tokens - no need to pass token
      const body = { user_id: userId, source };
      // Only pass token if explicitly provided (for new connections)
      if (source === 'youtube' && youtubeToken) {
        body.youtube_auth_token = youtubeToken;
      }
      // Backend will try to use stored tokens if no token provided
      
      console.log('Analyzing taste with:', { 
        source, 
        hasToken: !!youtubeToken,
        apiUrl,
        userId 
      });
      
      const response = await fetch(`${apiUrl}/api/taste/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include' // Include session cookies for Spotify
      });
      
      console.log('Taste analysis response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Taste analysis response:', data);
        if (data.taste) {
          // Log source information
          const sources = data.taste.sources || ['unknown'];
          const sourceDetails = data.taste.source_details || {};
          console.log(`[TASTE] Sources analyzed: ${sources.join(', ')}`);
          if (sourceDetails.spotify) {
            console.log(`[TASTE] Spotify: ${sourceDetails.spotify.artist_count} artists, ${sourceDetails.spotify.total_likes} total likes`);
          }
          if (sourceDetails.youtube) {
            console.log(`[TASTE] YouTube: ${sourceDetails.youtube.artist_count} artists, ${sourceDetails.youtube.total_likes} total likes`);
          }
          console.log(`[TASTE] Total merged artists: ${Object.keys(data.taste.artists || {}).length}`);
          setTaste(data.taste);
        } else {
          setError('No taste data returned');
        }
      } else {
        const errorText = await response.text();
        console.error('Taste analysis error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || 'Unknown error' };
        }
        setError(errorData.error || `Could not analyze taste from ${source}.`);
      }
    } catch (err) {
      console.error('Error analyzing taste:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      // More specific error messages
      if (err.message === 'Failed to fetch') {
        setError(`Cannot connect to backend server. Please check that the API is running at ${API_URL || process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000'}`);
      } else {
        setError(`Error analyzing taste from ${source}: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="taste-profile loading">Loading your taste profile...</div>;
  }

  if (error && !taste) {
    return (
      <div className="taste-profile error">
        <p>{error}</p>
        <p className="hint">Connect a music service to build your taste profile</p>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button onClick={connectYouTube} className="btn-primary">
            Connect YouTube Music
          </button>
          <button onClick={connectSpotify} className="btn-primary">
            Connect Spotify
          </button>
        </div>
      </div>
    );
  }

  if (!taste && !loading) {
    // Only show connect buttons if accounts aren't connected
    const needsConnection = !accountStatus.spotify_connected && !accountStatus.youtube_connected;
    
    return (
      <div className="taste-profile empty">
        <h2>Your Taste Profile</h2>
        {needsConnection ? (
          <>
            <p>Connect Spotify or YouTube Music to discover your music taste</p>
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={connectYouTube} className="btn-primary" style={{ minWidth: '180px' }}>
                🎥 Connect YouTube Music
              </button>
              <button onClick={connectSpotify} className="btn-primary" style={{ minWidth: '180px' }}>
                🎵 Connect Spotify
              </button>
            </div>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Or go to <strong>Connected Accounts</strong> to manage your connections
            </p>
          </>
        ) : (
          <p>Analyzing your taste profile... This may take a moment.</p>
        )}
      </div>
    );
  }

  const topGenres = Object.entries(taste.genres || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  const topMoods = Object.entries(taste.moods || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  // Get source information for display
  const sources = taste.sources || [];
  const sourceDetails = taste.source_details || {};
  const hasMultipleSources = sources.length > 1;

  return (
    <div className="taste-profile">
      {/* Header with Refresh Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'var(--card-bg, #f9f9f9)',
        borderRadius: '8px',
        border: '1px solid var(--border, #e0e0e0)'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.75rem' }}>Your Taste Profile</h2>
          {taste && (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {Object.keys(taste.artists || {}).length} artists
            </p>
          )}
        </div>
        <button
          onClick={forceRefreshAnalysis}
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem',
            background: loading ? 'var(--text-secondary)' : 'var(--accent, #007bff)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.95rem',
            fontWeight: '500',
            transition: 'all 0.2s',
            opacity: loading ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.background = 'var(--accent-hover, #0056b3)';
              e.target.style.transform = 'scale(1.02)';
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.background = 'var(--accent, #007bff)';
              e.target.style.transform = 'scale(1)';
            }
          }}
        >
          {loading ? (
            <>⏳ Refreshing...</>
          ) : (
            <>🔄 Refresh Analysis</>
          )}
        </button>
      </div>
      
      {/* Show source information */}
      {sources.length > 0 && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem', 
          background: 'var(--bg-secondary)', 
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Data Sources:</strong> {sources.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' + ')}
              {hasMultipleSources && sourceDetails.spotify && sourceDetails.youtube && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Spotify: {sourceDetails.spotify.artist_count} artists • 
                  YouTube: {sourceDetails.youtube.artist_count} artists • 
                  Combined: {Object.keys(taste.artists || {}).length} unique artists
                </div>
              )}
            </div>
            {hasMultipleSources && sourceDetails.spotify && sourceDetails.youtube && 
             sourceDetails.spotify.artist_count === sourceDetails.youtube.artist_count && (
              <button 
                onClick={() => {
                  console.log('[TASTE] Manually triggering fresh analysis...');
                  loadTaste();
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Refresh Analysis
              </button>
            )}
          </div>
        </div>
      )}
      
      {taste.artists && Object.keys(taste.artists).length > 0 && (
        <div className="taste-section">
          <h3>Your Artists ({Object.keys(taste.artists).length})</h3>
          <div className="artists-list" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.5rem',
            marginTop: '1rem'
          }}>
            {Object.entries(taste.artists)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 50)
              .map(([artist, count]) => (
                <div key={artist} style={{ 
                  padding: '0.5rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}>
                  <span style={{ fontWeight: '500' }}>{artist}</span>
                  <span style={{ 
                    marginLeft: '0.5rem', 
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem'
                  }}>
                    ({count} {count === 1 ? 'like' : 'likes'})
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {taste.recently_liked_artists && Object.keys(taste.recently_liked_artists).length > 0 && (
        <div className="taste-section">
          <h3>Recently Liked Artists ({Object.keys(taste.recently_liked_artists).length})</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Artists you've liked 1-2 times (will appear in main list after 3+ likes)
          </p>
          <div className="artists-list" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.5rem',
            marginTop: '1rem'
          }}>
            {Object.entries(taste.recently_liked_artists)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 30)
              .map(([artist, count]) => (
                <div key={artist} style={{ 
                  padding: '0.5rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  opacity: 0.8
                }}>
                  <span style={{ fontWeight: '500' }}>{artist}</span>
                  <span style={{ 
                    marginLeft: '0.5rem', 
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem'
                  }}>
                    ({count} {count === 1 ? 'like' : 'likes'})
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="taste-section">
        <h3>Energy Level</h3>
      <div className="energy-bar">
        <div 
          className="energy-fill" 
          style={{ width: `${(taste.energy_level || 0) * 100}%` }}
        />
        <span className="energy-label">
          {Math.round((taste.energy_level || 0) * 100)}%
        </span>
      </div>
      </div>

      {topGenres.length > 0 && (
        <div className="taste-section">
          <h3>Top Genres</h3>
          <div className="genre-list">
            {topGenres.map(([genre, score]) => (
              <div key={genre} className="genre-item">
                <span className="genre-name">{genre}</span>
                <div className="genre-bar">
                  <div 
                    className="genre-fill" 
                    style={{ width: `${score * 100}%` }}
                  />
                </div>
                <span className="genre-score">{Math.round(score * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="taste-section">
        <h3>Moods</h3>
        <div className="mood-grid">
          {topMoods.map(([mood, score]) => (
            <div key={mood} className="mood-item">
              <span className="mood-name">{mood}</span>
              <div className="mood-bar">
                <div 
                  className="mood-fill" 
                  style={{ width: `${score * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {taste.preference_signals && (
        <div className="taste-section">
          <h3>Why You Like This</h3>
          <div className="signals-list">
            {Object.entries(taste.preference_signals).map(([signal, value]) => (
              <div key={signal} className="signal-item">
                <span className="signal-name">{signal.replace('_', ' ')}</span>
                <span className="signal-value">{Math.round(value * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TasteProfile;
